// src/components/instructor/ops/CalendarSyncSettings.js
//
// Pass 88 — Google Calendar connection management (Settings). Owner/admin only. Shows an honest
// connection state (Connected / Not connected / Expired / Error) read from the client-readable
// calendarConnections presentation doc (NO OAuth tokens, NO Google API call here — §4/§15), and offers
// Connect / Reconnect / Disconnect. Booking → calendar event is a PROJECTION; this panel never implies
// the calendar is authority. Loads lazily (only when Settings is open) so Operations render is unaffected.
import React, { useEffect, useState, useCallback, useRef } from 'react';
import { db } from '../../../firebase';
import { useAuth } from '../../../AuthContext';
import { getCalendarConnectionsForOwner } from '../../../utils/calendarConnections';
import { canManageCalendarConnections } from '../../../utils/platformAccess';
import { beginGoogleCalendarConnect, disconnectGoogleCalendar } from '../../../utils/googleCalendarClient';

const C = { t1: '#1C1B18', t2: '#6B6B63', t3: '#A3A299', border: 'rgba(0,0,0,0.08)', green: '#1D9E75', amber: '#C97D10', red: '#DC2626', blue: '#2563EB' };
const font = "'DM Sans',sans-serif";

const STATE_LABEL = {
  connected: { label: 'Connected', color: C.green },
  expired: { label: 'Expired — reconnect', color: C.amber },
  error: { label: 'Error', color: C.red },
  disabled: { label: 'Not connected', color: C.t3 },
};

export default function CalendarSyncSettings({ organizationId = '' }) {
  const { userData, currentUser } = useAuth();
  const currentUserId = currentUser?.uid || '';
  const canManage = canManageCalendarConnections(userData || {}, { currentUserId, ownerType: 'organization', ownerId: organizationId, organizationId });
  const [loading, setLoading] = useState(true);
  const [connection, setConnection] = useState(null);
  const [busy, setBusy] = useState('');
  const [err, setErr] = useState('');

  // Keep the latest userData in a ref so the fetch reads it without making the whole userData object a
  // dependency (AuthContext may return a fresh object each render; depending on it would re-fetch forever).
  const userDataRef = useRef(userData); userDataRef.current = userData;

  const load = useCallback(async () => {
    if (!organizationId || !canManage) { setLoading(false); return; }
    setLoading(true);
    try {
      const conns = await getCalendarConnectionsForOwner(db, { ownerType: 'organization', ownerId: organizationId, currentUserId, userData: userDataRef.current });
      setConnection((conns || []).find((c) => c.provider === 'google') || null);
    } catch (e) { setErr('Could not load calendar connection.'); }
    setLoading(false);
  }, [organizationId, canManage, currentUserId]);

  useEffect(() => { load(); }, [load]);

  if (!canManage) return null;

  const status = connection ? (connection.status || 'connected') : 'disabled';
  const meta = STATE_LABEL[status] || STATE_LABEL.disabled;
  const isConnected = status === 'connected';

  const onConnect = async () => {
    setErr(''); setBusy('connect');
    try { await beginGoogleCalendarConnect({ organizationId, ownerType: 'organization', ownerId: organizationId }); }
    catch (e) { setErr('Could not start Google Calendar connection.'); setBusy(''); }
  };
  const onDisconnect = async () => {
    setErr(''); setBusy('disconnect');
    try { await disconnectGoogleCalendar({ calendarConnectionId: connection?.calendarConnectionId, organizationId }); await load(); }
    catch (e) { setErr('Could not disconnect.'); }
    setBusy('');
  };

  return (
    <div data-testid="calendar-sync-settings" style={{ fontFamily: font, color: C.t1 }}>
      <div style={{ fontFamily: "'Bebas Neue',sans-serif", fontSize: 22, letterSpacing: 1, marginBottom: 4 }}>Google Calendar</div>
      <div style={{ fontSize: 13, color: C.t2, marginBottom: 14, maxWidth: 560, lineHeight: 1.5 }}>
        Project confirmed Voletus bookings into a Google Calendar as events. Bookings stay the source of
        truth — edits in Google are never read back into Voletus.
      </div>

      {loading ? (
        <div style={{ fontSize: 13, color: C.t3 }}>Loading…</div>
      ) : (
        <div style={{ display: 'flex', alignItems: 'center', gap: 14, flexWrap: 'wrap' }}>
          <span data-testid="calendar-status" style={{ display: 'inline-flex', alignItems: 'center', gap: 7, fontSize: 13, fontWeight: 700, color: meta.color }}>
            <span style={{ width: 9, height: 9, borderRadius: '50%', background: meta.color }} />
            {meta.label}
          </span>
          {isConnected && connection?.email ? <span style={{ fontSize: 13, color: C.t2 }}>{connection.email}</span> : null}
          {isConnected && connection?.lastSyncedAt ? <span style={{ fontSize: 12, color: C.t3 }}>Last sync recorded</span> : null}

          <div style={{ flex: 1 }} />

          {!isConnected ? (
            <button data-testid="calendar-connect" onClick={onConnect} disabled={busy === 'connect'} style={btn(C.blue)}>
              {busy === 'connect' ? 'Starting…' : 'Connect Google Calendar'}
            </button>
          ) : (
            <>
              <button data-testid="calendar-reconnect" onClick={onConnect} disabled={!!busy} style={btn('#fff', C.t1)}>Reconnect</button>
              <button data-testid="calendar-disconnect" onClick={onDisconnect} disabled={busy === 'disconnect'} style={btn('#fff', C.red)}>
                {busy === 'disconnect' ? 'Disconnecting…' : 'Disconnect'}
              </button>
            </>
          )}
        </div>
      )}
      {err ? <div data-testid="calendar-error" style={{ fontSize: 12, color: C.red, marginTop: 10 }}>{err}</div> : null}
    </div>
  );
}

function btn(bg, color = '#fff') {
  const outlined = bg === '#fff';
  return {
    padding: '8px 16px', borderRadius: 9, fontSize: 13, fontWeight: 700, fontFamily: font, cursor: 'pointer',
    background: bg, color: outlined ? color : '#fff', border: outlined ? `1px solid ${color === '#fff' ? C.border : color}` : 'none',
  };
}
